import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  AlertCircle,
  Archive,
  ArchiveRestore,
  Boxes,
  PackageMinus,
  Plus,
  Trash2,
} from "lucide-react"
import { useRef, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import {
  type Articulo,
  type Clase,
  FormularioArticulo,
} from "@/componentes/inventario/FormularioArticulo"
import { comoCantidad, monto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { Pestanas } from "@/componentes/ui/Pestanas"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { decimalOpcional } from "@/lib/decimales"
import { usarSesion } from "@/lib/sesion"
import { Campo } from "@/paginas/interno/Gastos"

// ⚠ `Articulo` y `Clase` viven con el formulario que los escribe, no aquí: había dos
// definiciones del mismo artículo y dos formularios, y el de las órdenes pedía menos
// campos. Un tipo por concepto, en un solo sitio.

type Resumen = {
  consumibles: number
  valor_consumibles: string
  por_reponer: number
  bienes: number
  valor_bienes: string
}

const VISTAS = [
  { valor: "CONSUMIBLE" as Clase, etiqueta: "Consumibles", icono: PackageMinus },
  { valor: "BIEN" as Clase, etiqueta: "Bienes", icono: Boxes },
]

const SUBTITULO: Record<Clase, string> = {
  CONSUMIBLE: "Lo que se agota y hay que reponer: papelería, limpieza, consumibles.",
  BIEN: "Lo que la agencia posee y no se consume: equipos, mobiliario, herramientas.",
}

/**
 * Inventario: el depósito, en dos vistas que no se mezclan.
 *
 * ⚠ **Consumibles y bienes son dos preguntas distintas**, y por eso son dos pestañas y
 * no una tabla con una columna de más. Del consumible importa cuántos quedan y cuándo
 * reponer; del bien, dónde está y quién responde por él. Juntarlos deja media lista
 * con columnas que no le aplican —existencia en una fotocopiadora, responsable en una
 * resma— y entonces la pantalla no contesta ninguna de las dos.
 *
 * ⚠ **Las entradas no se teclean aquí.** Lo comprado entra solo al ejecutar el pago
 * de su orden, con el precio de esa línea: una entrada a mano haría crecer el stock
 * sin que nadie lo hubiera pagado, y el valor del depósito dejaría de poder cuadrarse
 * con lo que salió del banco. Lo que se registra desde esta pantalla es la SALIDA —lo
 * que se consumió, o la baja de un bien— y el ajuste por conteo físico.
 *
 * ⚠ **La existencia no es una columna: se deriva de los movimientos**, igual que el
 * saldo de una cuenta bancaria. Es la regla 17 llevada al depósito.
 */
export function Inventario({ clase }: { clase?: Clase } = {}) {
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()
  const [propia, setPropia] = useState<Clase>("CONSUMIBLE")
  // ⚠ **Con `clase` la vista la manda el DEPARTAMENTO**, y entonces esta pantalla pierde
  // su título y sus pestañas: las pone la cabecera de «Cadena de suministros». Sin ella
  // —`/inventario`, la dirección vieja— se comporta como siempre. Dos pantallas para lo
  // mismo habrían sido dos sitios donde corregir la misma columna.
  const dentroDelDepartamento = clase !== undefined
  const vista = clase ?? propia
  const setVista = setPropia
  const [abierto, setAbierto] = useState(false)
  const [enEdicion, setEnEdicion] = useState<Articulo | null>(null)
  const [sacando, setSacando] = useState<Articulo | null>(null)
  /** Cuál se está a punto de borrar. Uno cada vez: dos confirmaciones abiertas confunden. */
  const [porBorrar, setPorBorrar] = useState<Articulo | null>(null)
  const caja = useRef<HTMLDivElement | null>(null)

  const puedeAdministrar = tienePermiso(usuario, "inventario:administrar")
  const puedeMover = tienePermiso(usuario, "inventario:mover")
  // ⚠ **Borrar NO es dar de baja**, y por eso es otro permiso: sólo lo lleva el
  // superadministrador. Lo que se usó se archiva, y su ficha sigue explicando los
  // movimientos y las órdenes de hace seis meses. Cosmético, como todo `tienePermiso`:
  // el servidor responde 404 sin él, y niega el borrado si el artículo tiene historial.
  const puedeEliminar = tienePermiso(usuario, "inventario:eliminar")
  const esBien = vista === "BIEN"

  const articulos = useQuery({
    queryKey: ["articulos", vista],
    queryFn: () => api.get<Articulo[]>(`/api/v1/articulos?clase=${vista}`),
  })
  const resumen = useQuery({
    queryKey: ["inventario-resumen"],
    queryFn: () => api.get<Resumen>("/api/v1/inventario/resumen"),
  })

  function refrescar() {
    clienteQuery.invalidateQueries({ queryKey: ["articulos"] })
    clienteQuery.invalidateQueries({ queryKey: ["inventario-resumen"] })
  }

  // Dar de baja: sale de las listas vivas y la ficha se queda. Es lo que hace
  // cualquiera que administre el depósito; borrar es otra cosa y otro permiso.
  const archivar = useMutation({
    meta: {
      exito: (_: unknown, { activo }: { activo: boolean }) =>
        activo ? "Artículo reactivado" : "Artículo archivado",
    },
    mutationFn: ({ id, activo }: { id: number; activo: boolean }) =>
      api.patch(`/api/v1/articulos/${id}`, { activo }),
    onSuccess: refrescar,
  })

  const eliminar = useMutation({
    meta: { exito: "Artículo borrado" },
    mutationFn: (id: number) => api.delete(`/api/v1/articulos/${id}`),
    onSuccess: () => {
      setPorBorrar(null)
      refrescar()
    },
  })

  if (articulos.isPending) {
    return <p className="py-6">Cargando…</p>
  }
  if (articulos.isError) {
    return (
      <p
        className="flex items-center gap-2 py-6"
        role="alert"
        style={{ color: "var(--destructive)" }}
      >
        <AlertCircle size={16} /> No se pudo cargar el depósito.
      </p>
    )
  }

  const filas = articulos.data

  return (
    <div>
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          {!dentroDelDepartamento && (
            <>
              <h1 className="text-xl font-semibold">Inventario</h1>
              <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
                {SUBTITULO[vista]}
              </p>
            </>
          )}
        </div>
        <div className="flex flex-wrap items-start gap-2">
          <Exportar
            titulo={esBien ? "Activos fijos" : "Consumibles del depósito"}
            subtitulo={
              resumen.data
                ? esBien
                  ? `${resumen.data.bienes} bienes · valor ${resumen.data.valor_bienes}`
                  : `${resumen.data.consumibles} consumibles · ${resumen.data.por_reponer} por reponer · valor ${resumen.data.valor_consumibles}`
                : undefined
            }
            contenido={caja}
          />
          {puedeAdministrar && (
            <Boton
              variante="primario"
              onClick={() => {
                setEnEdicion(null)
                setAbierto(true)
              }}
            >
              <Plus size={16} /> {esBien ? "Nuevo bien" : "Nuevo consumible"}
            </Boton>
          )}
        </div>
      </header>

      {!dentroDelDepartamento && (
        <Pestanas
          pestanas={VISTAS}
          activa={vista}
          onCambiar={(v) => setVista(v as Clase)}
          etiqueta="Vistas del inventario"
          idPanel="panel-inventario"
          prefijoId="pestana-inventario"
        />
      )}

      {resumen.data && (
        <p className="tarjeta my-4 text-sm">
          {esBien ? (
            <>
              <span className="tabular-nums">{resumen.data.bienes}</span> bienes · valor{" "}
              <span className="tabular-nums">{resumen.data.valor_bienes}</span>
            </>
          ) : (
            <>
              <span className="tabular-nums">{resumen.data.consumibles}</span> consumibles ·{" "}
              <span className="tabular-nums">{resumen.data.por_reponer}</span> por reponer · valor{" "}
              <span className="tabular-nums">{resumen.data.valor_consumibles}</span>
            </>
          )}
        </p>
      )}

      {/* ⚠ Las columnas no son las mismas, y ése es el motivo de separar las vistas:
          un bien no tiene punto de reposición y un consumible no tiene responsable. */}
      <div
        ref={caja}
        id="panel-inventario"
        role="tabpanel"
        aria-labelledby={`pestana-inventario-${vista}`}
      >
        <Tabla etiqueta={esBien ? "Bienes de la agencia" : "Consumibles del depósito"}>
          <Cabecera>
            <Th>Código</Th>
            <Th>{esBien ? "Bien" : "Artículo"}</Th>
            {esBien ? <Th>Responsable</Th> : <Th>Categoría</Th>}
            {esBien ? <Th>Ubicación</Th> : <Th derecha>Existencia</Th>}
            <Th derecha>{esBien ? "Cantidad" : "Costo promedio"}</Th>
            <Th derecha>Valor</Th>
            <Th derecha>
              {/* Sólo botones: fuera del archivo exportado. */}
              <span data-exportar="no">Acciones</span>
            </Th>
          </Cabecera>
          <Cuerpo>
            {filas.length === 0 && (
              <TablaVacia colSpan={7}>
                {esBien
                  ? "Todavía no hay bienes registrados. Entran al pagar la orden que los compró."
                  : "Todavía no hay consumibles. El primero suele ser la resma de hojas carta."}
              </TablaVacia>
            )}
            {filas.map((articulo) => (
              <Fila key={articulo.id} atenuada={!articulo.activo}>
                <Td numero>{articulo.codigo}</Td>
                <Td
                  data-exportar-valor={[
                    articulo.nombre,
                    articulo.por_reponer && "Reponer",
                    articulo.exento_iva && "Exento de IVA",
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                >
                  {articulo.nombre}
                  {articulo.por_reponer && (
                    <span className="ml-2">
                      <Insignia tono="aviso">Reponer</Insignia>
                    </span>
                  )}
                  {/* Se ve en la lista porque de aquí lo heredan las órdenes: un
                      artículo marcado mal deja de pagar IVA en todo lo que se compre. */}
                  {articulo.exento_iva && (
                    <span className="ml-2">
                      <Insignia tono="neutro">Exento de IVA</Insignia>
                    </span>
                  )}
                </Td>
                {esBien ? (
                  <Td>{articulo.responsable ?? "—"}</Td>
                ) : (
                  <Td>{articulo.categoria ?? "—"}</Td>
                )}
                {esBien ? (
                  <Td>{articulo.ubicacion ?? "—"}</Td>
                ) : (
                  <Td derecha numero>
                    {comoCantidad(articulo.existencia)} {articulo.unidad.toLowerCase()}
                  </Td>
                )}
                <Td
                  derecha
                  numero
                  data-exportar-valor={
                    esBien ? comoCantidad(articulo.existencia) : monto(articulo.costo_promedio)
                  }
                >
                  {esBien ? comoCantidad(articulo.existencia) : articulo.costo_promedio}
                </Td>
                <Td derecha numero data-exportar-valor={monto(articulo.valor)}>
                  {articulo.valor}
                </Td>
                <Td derecha>
                  <div className="flex flex-wrap justify-end gap-2">
                    {puedeMover && articulo.activo && (
                      <Boton onClick={() => setSacando(articulo)}>
                        <PackageMinus size={16} /> {esBien ? "Dar de baja" : "Salida"}
                      </Boton>
                    )}
                    {puedeAdministrar && (
                      <Boton
                        onClick={() => {
                          setEnEdicion(articulo)
                          setAbierto(true)
                        }}
                      >
                        Editar
                      </Boton>
                    )}
                    {/* ⚠ **Archivar es la salida normal, y por eso está antes que
                        borrar.** Saca el artículo de las listas vivas y deja su ficha
                        explicando los movimientos y las órdenes que lo citan. Se
                        desarchiva con el mismo botón: no es una puerta de un solo
                        sentido. */}
                    {puedeAdministrar && (
                      <Boton
                        disabled={archivar.isPending}
                        onClick={() =>
                          archivar.mutate({ id: articulo.id, activo: !articulo.activo })
                        }
                      >
                        {articulo.activo ? (
                          <>
                            <Archive size={16} /> Archivar
                          </>
                        ) : (
                          <>
                            <ArchiveRestore size={16} /> Reactivar
                          </>
                        )}
                      </Boton>
                    )}
                    {puedeEliminar &&
                      (porBorrar?.id === articulo.id ? (
                        <span className="flex items-center gap-2 text-xs">
                          ¿Borrar la ficha?
                          <Boton
                            variante="peligro"
                            disabled={eliminar.isPending}
                            onClick={() => eliminar.mutate(articulo.id)}
                          >
                            Sí, borrar
                          </Boton>
                          <Boton onClick={() => setPorBorrar(null)}>No</Boton>
                        </span>
                      ) : (
                        <Boton
                          variante="peligro"
                          title="Borra la ficha. Lo que ya se usó se archiva, no se borra."
                          onClick={() => setPorBorrar(articulo)}
                        >
                          <Trash2 size={16} /> Eliminar
                        </Boton>
                      ))}
                  </div>
                  {eliminar.error instanceof ErrorApi && porBorrar?.id === articulo.id && (
                    <p
                      className="mt-1 text-right text-xs"
                      role="alert"
                      style={{ color: "var(--destructive)" }}
                    >
                      {eliminar.error.message}
                    </p>
                  )}
                </Td>
              </Fila>
            ))}
          </Cuerpo>
        </Tabla>
      </div>

      <Modal
        abierto={abierto}
        titulo={
          enEdicion ? `Editar ${enEdicion.codigo}` : esBien ? "Nuevo bien" : "Nuevo consumible"
        }
        descripcion="El artículo dice qué cosa es; el gasto dice en qué se gasta. La existencia sale de los movimientos."
        onCerrar={() => setAbierto(false)}
      >
        {abierto && (
          <FormularioArticulo
            key={enEdicion?.id ?? "nuevo"}
            articulo={enEdicion}
            clase={enEdicion?.clase ?? vista}
            onCerrar={() => setAbierto(false)}
            onGuardado={() => {
              refrescar()
              setAbierto(false)
            }}
          />
        )}
      </Modal>

      <Modal
        abierto={sacando !== null}
        titulo={sacando ? `Salida de ${sacando.nombre}` : "Salida"}
        descripcion="Lo que se consumió o se entregó. Se valora al costo promedio al que está el artículo."
        onCerrar={() => setSacando(null)}
      >
        {sacando !== null && (
          <FormularioSalida
            articulo={sacando}
            onCerrar={() => setSacando(null)}
            onGuardado={() => {
              refrescar()
              setSacando(null)
            }}
          />
        )}
      </Modal>
    </div>
  )
}

function FormularioSalida({
  articulo,
  onCerrar,
  onGuardado,
}: {
  articulo: Articulo
  onCerrar: () => void
  onGuardado: () => void
}) {
  const [esAjuste, setEsAjuste] = useState(false)

  const guardar = useMutation({
    meta: { exito: "Movimiento de inventario registrado" },
    mutationFn: (cuerpo: Record<string, unknown>) =>
      api.post("/api/v1/inventario/movimientos", cuerpo),
    onSuccess: onGuardado,
  })

  function enviar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    const datos = new FormData(evento.currentTarget)
    const texto = (campo: string) => {
      const valor = String(datos.get(campo) ?? "").trim()
      return valor === "" ? null : valor
    }
    guardar.mutate({
      articulo_id: articulo.id,
      tipo: esAjuste ? "AJUSTE" : "SALIDA",
      cantidad: decimalOpcional(texto("cantidad")),
      concepto: texto("concepto"),
      entregado_a: texto("entregado_a"),
      fecha_movimiento: texto("fecha_movimiento"),
    })
  }

  const error = guardar.error instanceof ErrorApi ? guardar.error.message : null

  return (
    <form className="space-y-4" onSubmit={enviar}>
      <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
        Quedan <span className="tabular-nums">{comoCantidad(articulo.existencia)}</span>{" "}
        {articulo.unidad.toLowerCase()}, a{" "}
        <span className="tabular-nums">{articulo.costo_promedio}</span> de promedio.
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <Campo para="sal-cantidad" etiqueta="Cantidad">
          <input
            id="sal-cantidad"
            name="cantidad"
            required
            inputMode="decimal"
            className="campo mt-1 tabular-nums"
          />
        </Campo>
        <Campo para="sal-fecha" etiqueta="Fecha" ayuda="Vacío es hoy.">
          <input id="sal-fecha" name="fecha_movimiento" type="date" className="campo mt-1" />
        </Campo>
      </div>
      <Campo
        para="sal-concepto"
        etiqueta="Concepto"
        ayuda="Para qué salió: quien lo lea en marzo tiene que entenderlo."
      >
        <input id="sal-concepto" name="concepto" required maxLength={200} className="campo mt-1" />
      </Campo>
      <Campo para="sal-entregado" etiqueta="Entregado a">
        <input id="sal-entregado" name="entregado_a" maxLength={150} className="campo mt-1" />
      </Campo>
      <Campo para="sal-ajuste" etiqueta="Tipo de movimiento">
        <label className="mt-1 flex items-center gap-2 text-sm" htmlFor="sal-ajuste">
          <input
            id="sal-ajuste"
            type="checkbox"
            checked={esAjuste}
            onChange={(e) => setEsAjuste(e.target.checked)}
          />
          Es un ajuste por conteo físico, no un consumo
        </label>
      </Campo>

      {error && (
        <p className="text-sm" style={{ color: "var(--destructive)" }}>
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Boton onClick={onCerrar}>Cancelar</Boton>
        <Boton type="submit" variante="primario" disabled={guardar.isPending}>
          {guardar.isPending ? "Registrando…" : "Registrar"}
        </Boton>
      </div>
    </form>
  )
}
